import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import axios from "axios";
import { Shield, ArrowLeft, Lock } from "lucide-react";
import { toast } from "sonner";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import Header from "../components/Header";
import Footer from "../components/Footer";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS, THIS BREAKS THE AUTH

const AdminSetup = () => {
  const navigate = useNavigate();
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [adminKey, setAdminKey] = useState("");
  const [claiming, setClaiming] = useState(false);

  useEffect(() => {
    const checkAuth = async () => {
      try {
        const res = await axios.get(`${API}/auth/me`, { withCredentials: true });
        setUser(res.data);
        
        // If already admin, redirect to admin dashboard
        if (res.data.role === "admin") {
          navigate("/admin");
        }
      } catch (error) {
        // Not logged in
      } finally {
        setLoading(false);
      }
    };
    checkAuth();
  }, [navigate]);

  const handleLogin = () => {
    const redirectUrl = window.location.origin + "/admin-setup";
    window.location.href = `https://auth.emergentagent.com/?redirect=${encodeURIComponent(redirectUrl)}`;
  };

  const handleClaimAdmin = async (e) => {
    e.preventDefault();
    setClaiming(true);

    try {
      const res = await axios.post(
        `${API}/auth/claim-admin`,
        { admin_key: adminKey },
        { withCredentials: true }
      );
      
      setUser(res.data);
      toast.success("Admin access granted!");
      navigate("/admin");
    } catch (error) {
      toast.error(error.response?.data?.detail || "Failed to claim admin access");
    } finally {
      setClaiming(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-[#F9F9F7]">
        <Header user={user} />
        <div className="pt-24 flex items-center justify-center h-[60vh]">
          <div className="w-12 h-12 border-4 border-[#1A4D2E] border-t-transparent rounded-full animate-spin"></div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#F9F9F7]" data-testid="admin-setup-page">
      <Header user={user} />

      <main className="pt-24 lg:pt-28 pb-16 px-6 lg:px-12">
        <div className="max-w-md mx-auto">
          <div className="bg-white rounded-2xl shadow-sm border border-stone-200 p-8">
            <div className="flex items-center justify-center mb-6">
              <div className="w-16 h-16 bg-[#1A4D2E]/10 rounded-2xl flex items-center justify-center">
                <Shield className="w-8 h-8 text-[#1A4D2E]" />
              </div>
            </div>

            <h1 className="text-2xl font-bold text-[#1A1A1A] text-center mb-2">
              Admin Access
            </h1>
            <p className="text-[#71717A] text-center mb-8">
              {user 
                ? "Enter your admin key to access the admin panel" 
                : "Sign in first to access admin features"}
            </p>

            {!user ? (
              <Button
                onClick={handleLogin}
                className="w-full bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full py-6"
                data-testid="admin-login-btn"
              >
                Sign In with Google
              </Button>
            ) : (
              <form onSubmit={handleClaimAdmin} className="space-y-4">
                <div>
                  <Label htmlFor="adminKey" className="text-[#1A1A1A]">
                    Admin Key
                  </Label>
                  <div className="relative mt-1.5">
                    <Lock className="absolute left-3 top-1/2 -translate-y-1/2 w-5 h-5 text-[#71717A]" />
                    <Input
                      id="adminKey"
                      type="password"
                      value={adminKey}
                      onChange={(e) => setAdminKey(e.target.value)}
                      placeholder="Enter admin key"
                      className="pl-10"
                      data-testid="admin-key-input"
                    />
                  </div>
                  <p className="text-xs text-[#71717A] mt-2">
                    First-time setup? Leave blank if you're the first admin.
                  </p>
                </div>

                <Button
                  type="submit"
                  disabled={claiming}
                  className="w-full bg-[#1A4D2E] hover:bg-[#143D24] text-white rounded-full py-6"
                  data-testid="claim-admin-btn"
                >
                  {claiming ? "Verifying..." : "Access Admin Panel"}
                </Button>

                <div className="text-center pt-4">
                  <p className="text-sm text-[#71717A]">
                    Signed in as: <span className="font-medium text-[#1A1A1A]">{user.email}</span>
                  </p>
                </div>
              </form>
            )}

            <div className="mt-6 pt-6 border-t border-stone-200">
              <Button
                variant="ghost"
                onClick={() => navigate("/")}
                className="w-full text-[#71717A]"
              >
                <ArrowLeft className="w-4 h-4 mr-2" />
                Back to Home
              </Button>
            </div>
          </div>
        </div>
      </main>

      <Footer />
    </div>
  );
};

export default AdminSetup;
